import { useState } from 'react';
import { ALargeSmall, ChevronDown, Fingerprint, KeyRound, LayoutGrid, LogOut, Moon, Search, Sun } from 'lucide-react';
import {
  Avatar,
  AvatarFallback,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Kbd,
  Separator,
  SidebarTrigger,
} from '@proerp/ui';
import { useUiStore } from '@/store/uiStore';
import { usePublicBranding } from '@/features/settings/settingsApi';
import { useTheme } from '@/lib/theme';
import { TEXT_SIZE_LABELS, useTextSize, type TextSize } from '@/lib/textSize';
import { logout } from '@/lib/auth';
import { initials } from '@/lib/format';
import { formatShortcut } from '@/lib/platformKeys';
import { SHORTCUT_KEYS } from '@/lib/shortcuts';
import { useCurrentUser } from '@/lib/useCurrentUser';
import { useKeyPlatform } from '@/lib/useKeyPlatform';
import { ChangePasswordDialog } from './ChangePasswordDialog';
import { ModuleVisibilityDialog } from './ModuleVisibilityDialog';
import { SecuritySettingsDialog } from './SecuritySettingsDialog';
import { NotificationBell } from './NotificationBell';
import { TabBar } from './TabBar';
import { TimerPill } from './TimerPill';

/**
 * Site-Header (Muster: shadcn dashboard-01), Sidebar-Trigger, Suche (⌘K, ADR 0015).
 * Das Benutzermenü sitzt desktop im Sidebar-Footer (NavUser); mobil (ohne
 * Sidebar) zeigt der Header Branding + Benutzermenü selbst.
 *
 * Ab `tablet` eine einzige Zeile (44 px, Kopfzeile A, 2026-09-30): Die Tabs
 * (ADR 0111) stehen in der Leiste, das Suchfeld ist ein Knopf „Suchen ⌘K“ vor
 * den Symbolen. Vorher zwei Zeilen (48 + 40 px) — zu viel Platz für den Kopf.
 * Die Höhe steht als `--app-header-h` an der App-Hülle (Vollhöhen-Seiten).
 */
export function Topbar() {
  const openSearch = useUiStore((state) => state.setSearchOpen);
  const appLogoDataUrl = usePublicBranding().data?.data.appLogoDataUrl ?? null;
  const keyPlatform = useKeyPlatform();

  return (
    <header className="flex h-12 shrink-0 items-center gap-2 border-b bg-background transition-[width,height] ease-linear tablet:h-11 tablet:bg-muted/60 tablet:touch:h-12">
      <div className="flex h-full w-full min-w-0 items-center gap-1 px-4 tablet:pr-3 lg:gap-2 lg:pl-6">
        {/* Ab `tablet` (744px, iPad mini im Hochformat) die einzige Möglichkeit,
            die Sidebar ein-/auszuklappen (§ Tablet-Optimierung) — auf Touch auf
            36px angehoben (28px sonst). */}
        <SidebarTrigger className="-ml-1 hidden tablet:flex touch:size-9" aria-label="Navigation ein-/ausklappen" />
        <Separator orientation="vertical" className="mx-2 hidden h-4 tablet:block" />

        {/* Branding nur mobil — Desktop hat es im Sidebar-Header. `shrink` +
            `min-w-0`, damit die Symbolreihe rechts Vorrang hat: Mit laufendem
            Timer steht dort eine Pille mehr, und bei 390 px war der Avatar
            vorher angeschnitten. */}
        <div className="flex min-w-0 shrink items-center gap-2 tablet:hidden">
          {/* Logo: `min-w-0`, sonst schrumpft das Bild als Flex-Element nie unter
              seine Eigenbreite, und die Symbolreihe schiebt sich darüber (2026-09-28). */}
          {appLogoDataUrl ? (
            <img src={appLogoDataUrl} alt="Logo" className="h-6 min-w-0 max-w-28 object-contain object-left" />
          ) : (
            <>
              <span className="truncate text-md font-semibold text-foreground">ProERP</span>
              <span className="hidden rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground min-[420px]:inline">
                Beta
              </span>
            </>
          )}
        </div>

        {/* Tabs füllen die Zeile; die Leiste scrollt selbst, wenn es eng wird. */}
        <TabBar />

        {/* Suche als Knopf: am iPad hochkant nur die Lupe, ab `lg` mit Beschriftung
            und Kürzel. `shrink-0`, die Tabs geben Platz ab, nicht die Suche. */}
        <button
          type="button"
          onClick={() => openSearch(true)}
          aria-label="Suche öffnen"
          title={`Suche (${formatShortcut(SHORTCUT_KEYS.openSearch, keyPlatform)})`}
          className="hidden h-8 shrink-0 items-center gap-2 rounded-md border border-input bg-background px-2 text-sm text-muted-foreground transition-colors hover:bg-muted/70 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring tablet:flex touch:h-9 touch:min-h-0 lg:px-2.5"
        >
          <Search className="size-4 shrink-0" strokeWidth={1.5} aria-hidden />
          <span className="hidden lg:inline">Suchen</span>
          <Kbd className="hidden lg:inline-flex">{formatShortcut(SHORTCUT_KEYS.openSearch, keyPlatform)}</Kbd>
        </button>

        <div className="ml-auto flex shrink-0 items-center gap-0.5 tablet:ml-0 tablet:gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="size-8 tablet:hidden"
            onClick={() => openSearch(true)}
            aria-label="Suche öffnen"
          >
            <Search strokeWidth={1.5} />
          </Button>
          <TimerPill />
          <NotificationBell />
          <TextSizeMenu />
          <ThemeToggle />
          <MobileUserMenu />
        </div>
      </div>
    </header>
  );
}

/**
 * Textgrößen-Menü: iOS gibt die System-Textgröße (Dynamic Type) nicht an
 * Webinhalte weiter — dies ist der App-eigene Ersatz (persistiert, skaliert die
 * ganze App, siehe apps/web/src/lib/textSize.ts).
 */
function TextSizeMenu() {
  const textSize = useTextSize((state) => state.textSize);
  const setTextSize = useTextSize((state) => state.setTextSize);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="size-8" aria-label="Textgröße" title="Textgröße">
          <ALargeSmall strokeWidth={1.5} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>Textgröße</DropdownMenuLabel>
        <DropdownMenuRadioGroup value={textSize} onValueChange={(value) => setTextSize(value as TextSize)}>
          {(Object.keys(TEXT_SIZE_LABELS) as TextSize[]).map((size) => (
            <DropdownMenuRadioItem key={size} value={size}>
              {TEXT_SIZE_LABELS[size]}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Hell/Dunkel-Umschalter (persistiert; respektiert anfangs die Systemeinstellung). */
function ThemeToggle() {
  const theme = useTheme((state) => state.theme);
  const toggle = useTheme((state) => state.toggle);
  const isDark = theme === 'dark';
  return (
    <Button
      variant="ghost"
      size="icon"
      className="size-8"
      onClick={toggle}
      aria-label={isDark ? 'Helles Design' : 'Dunkles Design'}
      title={isDark ? 'Helles Design' : 'Dunkles Design'}
    >
      {isDark ? <Sun strokeWidth={1.5} /> : <Moon strokeWidth={1.5} />}
    </Button>
  );
}

/**
 * Benutzermenü für mobile Ansichten — desktop übernimmt NavUser in der Sidebar.
 *
 * ACHTUNG: Die Einträge hier und in `NavUser` müssen deckungsgleich bleiben.
 * Fehlt einer, ist die Funktion auf dem jeweiligen Gerät unerreichbar —
 * 2026-08-24 fehlte „Sicherheit", womit sich auf dem iPhone kein Passkey
 * einrichten ließ. `Topbar.spec.tsx` hält die Liste jetzt fest.
 */
export function MobileUserMenu() {
  const { data } = useCurrentUser();
  const me = data?.data;
  const display = me?.name || me?.email || 'Benutzer';
  const isRealSession = Boolean(me && !me.isDevSession);
  const [isPasswordOpen, setIsPasswordOpen] = useState(false);
  const [isModulesOpen, setIsModulesOpen] = useState(false);
  const [isSecurityOpen, setIsSecurityOpen] = useState(false);

  const avatar = (
    <Avatar className="size-7">
      <AvatarFallback className="bg-accent text-xs font-medium text-foreground">
        {initials(display)}
      </AvatarFallback>
    </Avatar>
  );

  if (!isRealSession) {
    return (
      <div className="flex items-center gap-2 px-1.5 tablet:hidden">
        {avatar}
        <div className="text-right">
          <p className="text-sm leading-tight text-foreground">{display}</p>
          {me?.isDevSession && (
            <p className="text-xs leading-tight text-warning-500">Dev-Session</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="tablet:hidden">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="gap-1.5 px-1" aria-label="Benutzermenü">
            {avatar}
            <ChevronDown className="!size-3.5 text-muted-foreground" aria-hidden />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-60">
          <DropdownMenuLabel className="font-normal">
            <span className="block truncate text-sm font-medium text-foreground">{display}</span>
            {me?.email && (
              <span className="block truncate text-xs text-muted-foreground">{me.email}</span>
            )}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuGroup>
            <DropdownMenuItem onSelect={() => setTimeout(() => setIsPasswordOpen(true), 0)}>
              <KeyRound strokeWidth={1.5} aria-hidden />
              Passwort ändern
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => setTimeout(() => setIsSecurityOpen(true), 0)}>
              <Fingerprint strokeWidth={1.5} aria-hidden />
              Sicherheit
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => setTimeout(() => setIsModulesOpen(true), 0)}>
              <LayoutGrid strokeWidth={1.5} aria-hidden />
              Module verwalten
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => logout()}>
              <LogOut strokeWidth={1.5} aria-hidden />
              Abmelden
            </DropdownMenuItem>
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
      <ChangePasswordDialog open={isPasswordOpen} onOpenChange={setIsPasswordOpen} />
      <ModuleVisibilityDialog open={isModulesOpen} onOpenChange={setIsModulesOpen} />
      <SecuritySettingsDialog open={isSecurityOpen} onOpenChange={setIsSecurityOpen} />
    </div>
  );
}
